import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileText, CalendarRange, Filter, X } from 'lucide-react';
import { toast } from 'sonner';

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso || ''; } };

const STATUS_STYLE = {
  released: 'text-green-700', paid: 'text-green-700',
  pending: 'text-amber-600', forfeited: 'text-red-500',
};

export const BusinessReports = () => {
  // ---- Income / commission report ----
  const [incFrom, setIncFrom] = useState('');
  const [incTo, setIncTo] = useState('');
  const [income, setIncome] = useState(null);
  const [incLoading, setIncLoading] = useState(false);

  const loadIncome = useCallback(async (from, to) => {
    setIncLoading(true);
    try {
      const q = new URLSearchParams();
      if (from) q.set('from_date', from);
      if (to) q.set('to_date', to);
      const { data } = await api.get(`/business/income-report${q.toString() ? `?${q}` : ''}`);
      setIncome(data);
    } catch { toast.error('Could not load income report'); }
    finally { setIncLoading(false); }
  }, []);

  // ---- Weekly business report ----
  const [wkFrom, setWkFrom] = useState('');
  const [wkTo, setWkTo] = useState('');
  const [weekly, setWeekly] = useState(null);
  const [wkSel, setWkSel] = useState('all');
  const [wkLoading, setWkLoading] = useState(false);

  const loadWeekly = useCallback(async (from, to) => {
    setWkLoading(true);
    try {
      const q = new URLSearchParams();
      if (from) q.set('from_date', from);
      if (to) q.set('to_date', to);
      const { data } = await api.get(`/business/weekly-report${q.toString() ? `?${q}` : ''}`);
      setWeekly(data);
      setWkSel('all');
    } catch { toast.error('Could not load weekly report'); }
    finally { setWkLoading(false); }
  }, []);

  useEffect(() => { loadIncome(); loadWeekly(); }, [loadIncome, loadWeekly]);

  const incTotals = income?.totals || {};
  const weeks = weekly?.weeks || [];
  const shownWeeks = wkSel === 'all' ? weeks : weeks.filter(w => String(w.week_no) === String(wkSel));

  return (
    <>
      {/* Income / commission report */}
      <Card className="p-4" data-testid="income-report-card">
        <div className="font-bold display mb-1 flex items-center gap-2"><FileText className="h-4 w-4 text-[#1D4ED8]" />Income &amp; commission report</div>
        <div className="text-[11px] text-[var(--bz-muted)] mb-3">Filter your referral &amp; matching income by a date range.</div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] block mb-0.5">From</label>
            <Input type="date" value={incFrom} onChange={e => setIncFrom(e.target.value)} className="h-9 w-40" data-testid="income-from-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] block mb-0.5">To</label>
            <Input type="date" value={incTo} onChange={e => setIncTo(e.target.value)} className="h-9 w-40" data-testid="income-to-date" />
          </div>
          <Button onClick={() => loadIncome(incFrom, incTo)} disabled={incLoading} className="h-9 rounded-full bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-1" data-testid="income-apply-btn"><Filter className="h-3.5 w-3.5" />Apply</Button>
          {(incFrom || incTo) && <Button variant="outline" onClick={() => { setIncFrom(''); setIncTo(''); loadIncome(); }} className="h-9 rounded-full gap-1" data-testid="income-clear-btn"><X className="h-3.5 w-3.5" />Clear</Button>}
        </div>

        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="rounded-xl bg-[var(--bz-soft)] p-2.5 text-center" data-testid="income-total-gross">
            <div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold">Gross</div>
            <div className="text-lg font-black display text-[#1E3A8A]">{money(incTotals.gross)}</div>
          </div>
          <div className="rounded-xl bg-[var(--bz-soft)] p-2.5 text-center" data-testid="income-total-tds">
            <div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold">TDS</div>
            <div className="text-lg font-black display text-orange-600">{money(incTotals.tds)}</div>
          </div>
          <div className="rounded-xl bg-[var(--bz-green-tint)] p-2.5 text-center" data-testid="income-total-net">
            <div className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] font-bold">Net</div>
            <div className="text-lg font-black display text-green-700">{money(incTotals.net)}</div>
          </div>
        </div>
        {(income?.by_type || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="income-by-type">
            {income.by_type.map(t => (
              <span key={t.type} className="text-[11px] rounded-full bg-[var(--bz-soft)] px-2.5 py-1"><b>{t.label}:</b> {money(t.amount)}</span>
            ))}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] text-left">
                <th className="py-1.5 pr-3">Date</th>
                <th className="py-1.5 pr-3">Type</th>
                <th className="py-1.5 pr-3">Gross</th>
                <th className="py-1.5 pr-3">TDS</th>
                <th className="py-1.5 pr-3">Net</th>
                <th className="py-1.5">Status</th>
              </tr>
            </thead>
            <tbody data-testid="income-rows">
              {(income?.rows || []).length === 0 && <tr><td colSpan={6} className="py-3 text-[var(--bz-muted)]">No income in this range.</td></tr>}
              {(income?.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-[var(--bz-line)]" data-testid={`income-row-${i}`}>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="py-1.5 pr-3">{r.label}</td>
                  <td className="py-1.5 pr-3">{money(r.amount)}</td>
                  <td className="py-1.5 pr-3 text-orange-600">{money(r.tds)}</td>
                  <td className="py-1.5 pr-3 text-green-700 font-semibold">{money(r.net)}</td>
                  <td className={`py-1.5 capitalize font-semibold ${STATUS_STYLE[r.status] || ''}`}>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Weekly business report */}
      <Card className="p-4" data-testid="weekly-report-card">
        <div className="font-bold display mb-1 flex items-center gap-2"><CalendarRange className="h-4 w-4 text-green-700" />Weekly business report</div>
        <div className="text-[11px] text-[var(--bz-muted)] mb-3">Pick a week number, or filter by a from → to date range (business week = Sat–Fri).</div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] block mb-0.5">Week</label>
            <select value={wkSel} onChange={e => setWkSel(e.target.value)} className="h-9 rounded-md border border-[var(--bz-line)] px-2 text-sm bg-white" data-testid="weekly-week-select">
              <option value="all">All weeks</option>
              {weeks.map(w => <option key={w.week} value={w.week_no}>Week {w.week_no} — {fmtDate(w.week)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] block mb-0.5">From</label>
            <Input type="date" value={wkFrom} onChange={e => setWkFrom(e.target.value)} className="h-9 w-40" data-testid="weekly-from-date" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] block mb-0.5">To</label>
            <Input type="date" value={wkTo} onChange={e => setWkTo(e.target.value)} className="h-9 w-40" data-testid="weekly-to-date" />
          </div>
          <Button onClick={() => loadWeekly(wkFrom, wkTo)} disabled={wkLoading} className="h-9 rounded-full bg-green-700 hover:bg-green-800 gap-1" data-testid="weekly-apply-btn"><Filter className="h-3.5 w-3.5" />Apply</Button>
          {(wkFrom || wkTo) && <Button variant="outline" onClick={() => { setWkFrom(''); setWkTo(''); loadWeekly(); }} className="h-9 rounded-full gap-1" data-testid="weekly-clear-btn"><X className="h-3.5 w-3.5" />Clear</Button>}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[var(--bz-muted)] text-left">
                <th className="py-1.5 pr-3">Wk</th>
                <th className="py-1.5 pr-3">Week of</th>
                <th className="py-1.5 pr-3">Personal ₹</th>
                <th className="py-1.5 pr-3">Personal BV</th>
                <th className="py-1.5 pr-3">Team ₹</th>
                <th className="py-1.5 pr-3">Team BV</th>
                <th className="py-1.5">Income (net)</th>
              </tr>
            </thead>
            <tbody data-testid="weekly-report-rows">
              {shownWeeks.length === 0 && <tr><td colSpan={7} className="py-3 text-[var(--bz-muted)]">No activity in this range.</td></tr>}
              {shownWeeks.map(w => (
                <tr key={w.week} className="border-t border-[var(--bz-line)]" data-testid={`weekly-report-row-${w.week_no}`}>
                  <td className="py-1.5 pr-3 font-bold">{w.week_no}</td>
                  <td className="py-1.5 pr-3 whitespace-nowrap font-semibold">{fmtDate(w.week)}</td>
                  <td className="py-1.5 pr-3">{money(w.personal_total)}</td>
                  <td className="py-1.5 pr-3">{w.personal_bv}</td>
                  <td className="py-1.5 pr-3 text-green-700">{money(w.team_total)}</td>
                  <td className="py-1.5 pr-3 text-green-700">{w.team_bv}</td>
                  <td className="py-1.5 text-green-700 font-semibold">{money(w.income_net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};
